/**
 * TCF Canada Scoring Utilities
 * Conversion des scores bruts en points TCF et niveaux CECRL/NCLC
 */

// Grille TCF officielle pour Compréhension Écrite
const TCF_GRID_CE = [
  { cecrl: 'C2', nclc: '10+', minScore: 549, maxScore: 699 },
  { cecrl: 'C1', nclc: '9', minScore: 524, maxScore: 548 },
  { cecrl: 'B2', nclc: '8', minScore: 499, maxScore: 523 },
  { cecrl: 'B2', nclc: '7', minScore: 453, maxScore: 498 },
  { cecrl: 'B1', nclc: '6', minScore: 406, maxScore: 452 },
  { cecrl: 'B1', nclc: '5', minScore: 375, maxScore: 405 },
  { cecrl: 'A2', nclc: '4', minScore: 342, maxScore: 374 },
  { cecrl: 'A2', nclc: '3', minScore: 300, maxScore: 341 },
  { cecrl: 'A1', nclc: '2', minScore: 200, maxScore: 299 },
  { cecrl: 'A1', nclc: '1', minScore: 0, maxScore: 199 },
];

// Grille TCF officielle pour Compréhension Orale
const TCF_GRID_CO = [
  { cecrl: 'C2', nclc: '10+', minScore: 549, maxScore: 699 },
  { cecrl: 'C1', nclc: '9', minScore: 523, maxScore: 548 },
  { cecrl: 'B2', nclc: '8', minScore: 503, maxScore: 522 },
  { cecrl: 'B2', nclc: '7', minScore: 458, maxScore: 502 },
  { cecrl: 'B1', nclc: '6', minScore: 398, maxScore: 457 },
  { cecrl: 'B1', nclc: '5', minScore: 369, maxScore: 397 },
  { cecrl: 'A2', nclc: '4', minScore: 331, maxScore: 368 },
  { cecrl: 'A2', nclc: '3', minScore: 298, maxScore: 330 },
  { cecrl: 'A1', nclc: '2', minScore: 200, maxScore: 297 },
  { cecrl: 'A1', nclc: '1', minScore: 0, maxScore: 199 },
];

// Grille TCF officielle pour Expression Écrite (sur 20 points)
const TCF_GRID_EE = [
  { cecrl: 'C2', nclc: '10+', minScore: 16, maxScore: 20 },
  { cecrl: 'C1', nclc: '9', minScore: 14, maxScore: 15 },
  { cecrl: 'B2', nclc: '8', minScore: 12, maxScore: 13 },
  { cecrl: 'B2', nclc: '7', minScore: 10, maxScore: 11 },
  { cecrl: 'B1', nclc: '6', minScore: 7, maxScore: 9 },
  { cecrl: 'B1', nclc: '5', minScore: 6, maxScore: 6 },
  { cecrl: 'A2', nclc: '4', minScore: 4, maxScore: 5 },
  { cecrl: 'A2', nclc: '3', minScore: 3, maxScore: 3 },
  { cecrl: 'A1', nclc: '2', minScore: 2, maxScore: 2 },
  { cecrl: 'A1', nclc: '1', minScore: 0, maxScore: 1 },
];

// Grille TCF officielle pour Expression Orale (sur 20 points)
const TCF_GRID_EO = [
  { cecrl: 'C2', nclc: '10+', minScore: 16, maxScore: 20 },
  { cecrl: 'C1', nclc: '9', minScore: 14, maxScore: 15 },
  { cecrl: 'B2', nclc: '8', minScore: 12, maxScore: 13 },
  { cecrl: 'B2', nclc: '7', minScore: 10, maxScore: 11 },
  { cecrl: 'B1', nclc: '6', minScore: 7, maxScore: 9 },
  { cecrl: 'B1', nclc: '5', minScore: 6, maxScore: 6 },
  { cecrl: 'A2', nclc: '4', minScore: 4, maxScore: 5 },
  { cecrl: 'A2', nclc: '3', minScore: 3, maxScore: 3 },
  { cecrl: 'A1', nclc: '2', minScore: 2, maxScore: 2 },
  { cecrl: 'A1', nclc: '1', minScore: 0, maxScore: 1 },
];

/**
 * Convertit un score brut (points obtenus sur max) en score TCF (0-699)
 * @param {number} rawScore - Score brut obtenu
 * @param {number} maxScore - Score maximum possible
 * @returns {number} - Score TCF (0-699)
 */
export function convertToTCFScore(rawScore, maxScore) {
  if (maxScore === 0) return 0;
  const percentage = rawScore / maxScore;
  // Le score TCF va de 0 à 699
  return Math.round(percentage * 699);
}

/**
 * Obtient le niveau CECRL/NCLC basé sur le score TCF pour Compréhension Écrite
 * @param {number} tcfScore - Score TCF (0-699)
 * @returns {Object} - { cecrl, nclc, color }
 */
export function getCECRLLevelCE(tcfScore) {
  for (const level of TCF_GRID_CE) {
    if (tcfScore >= level.minScore && tcfScore <= level.maxScore) {
      return {
        cecrl: level.cecrl,
        nclc: level.nclc,
        color: getLevelColor(level.cecrl)
      };
    }
  }
  return { cecrl: 'A1', nclc: '1', color: getLevelColor('A1') };
}

/**
 * Obtient le niveau CECRL/NCLC basé sur le score TCF pour Compréhension Orale
 * @param {number} tcfScore - Score TCF (0-699)
 * @returns {Object} - { cecrl, nclc, color }
 */
export function getCECRLLevelCO(tcfScore) {
  for (const level of TCF_GRID_CO) {
    if (tcfScore >= level.minScore && tcfScore <= level.maxScore) {
      return {
        cecrl: level.cecrl,
        nclc: level.nclc,
        color: getLevelColor(level.cecrl)
      };
    }
  }
  return { cecrl: 'A1', nclc: '1', color: getLevelColor('A1') };
}

/**
 * Obtient le niveau CECRL/NCLC basé sur le score pour Expression Écrite (sur 20)
 * @param {number} score - Score (0-20)
 * @returns {Object} - { cecrl, nclc, color }
 */
export function getCECRLLevelEE(score) {
  for (const level of TCF_GRID_EE) {
    if (score >= level.minScore && score <= level.maxScore) {
      return {
        cecrl: level.cecrl,
        nclc: level.nclc,
        color: getLevelColor(level.cecrl)
      };
    }
  }
  return { cecrl: 'A1', nclc: '1', color: getLevelColor('A1') };
}

/**
 * Obtient le niveau CECRL/NCLC basé sur le score pour Expression Orale (sur 20)
 * @param {number} score - Score (0-20)
 * @returns {Object} - { cecrl, nclc, color }
 */
export function getCECRLLevelEO(score) {
  for (const level of TCF_GRID_EO) {
    if (score >= level.minScore && score <= level.maxScore) {
      return {
        cecrl: level.cecrl,
        nclc: level.nclc,
        color: getLevelColor(level.cecrl)
      };
    }
  }
  return { cecrl: 'A1', nclc: '1', color: getLevelColor('A1') };
}

/**
 * Obtient la couleur associée à un niveau CECRL
 * @param {string} cecrl - Niveau CECRL (A1, A2, B1, B2, C1, C2)
 * @returns {Object} - { bg, text, border }
 */
export function getLevelColor(cecrl) {
  const colors = {
    'C2': { 
      bg: 'bg-purple-500', 
      bgLight: 'bg-purple-100 dark:bg-purple-900/30',
      text: 'text-purple-600 dark:text-purple-400', 
      border: 'border-purple-500' 
    },
    'C1': { 
      bg: 'bg-blue-500', 
      bgLight: 'bg-blue-100 dark:bg-blue-900/30',
      text: 'text-blue-600 dark:text-blue-400', 
      border: 'border-blue-500' 
    },
    'B2': { 
      bg: 'bg-green-500', 
      bgLight: 'bg-green-100 dark:bg-green-900/30',
      text: 'text-green-600 dark:text-green-400', 
      border: 'border-green-500' 
    },
    'B1': { 
      bg: 'bg-yellow-500', 
      bgLight: 'bg-yellow-100 dark:bg-yellow-900/30',
      text: 'text-yellow-600 dark:text-yellow-400', 
      border: 'border-yellow-500' 
    },
    'A2': { 
      bg: 'bg-orange-500', 
      bgLight: 'bg-orange-100 dark:bg-orange-900/30',
      text: 'text-orange-600 dark:text-orange-400', 
      border: 'border-orange-500' 
    },
    'A1': { 
      bg: 'bg-red-500', 
      bgLight: 'bg-red-100 dark:bg-red-900/30',
      text: 'text-red-600 dark:text-red-400', 
      border: 'border-red-500' 
    },
  };
  return colors[cecrl] || colors['A1'];
}

/**
 * Formate le score pour affichage
 * @param {number} tcfScore - Score TCF
 * @param {Object} level - Niveau CECRL/NCLC
 * @returns {string} - Texte formaté
 */
export function formatScoreDisplay(tcfScore, level) {
  return `${tcfScore}/699 • ${level.cecrl} (NCLC ${level.nclc})`;
}

// Export des grilles pour référence
export const TCF_GRIDS = {
  CE: TCF_GRID_CE,
  CO: TCF_GRID_CO,
  EE: TCF_GRID_EE,
  EO: TCF_GRID_EO
};
